<template>
    <div class="animated fadeIn">
        <div
            v-show="loading"
            class="kgr-row kgr-text--centered"
        >
            <kgr-loading />
        </div>

        <div v-show="!loading">
            <div class="app-contentHeader kgr-hidden-print">
                <a
                    :href="backUrl"
                    class="kgr-button kgr-button--link"
                >
                    <kgr-icon
                        icon-class="arrow-left"
                        small
                    />
                    {{ $t('backToOverview') }}
                </a>
                <div class="kgr-postfix">
                    <a
                        v-if="translateTo && !loading"
                        class="kgr-button kgr-button--accent"
                        style="margin-right: 10px"
                        @click="showOriginal = !showOriginal"
                    >
                        {{ showOriginal ? $t('Übersetzung anzeigen') : $t('Originaltext anzeigen') }}
                    </a>
                    <a
                        v-if="hasGeneralWriteAccess"
                        :href="newAlbumUrl"
                        class="kgr-button kgr-button--accent"
                        style="margin-right: 10px"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        {{ $t('Neues Album hinzufügen') }}
                    </a>
                    <a
                        v-if="album.hasUploadAccess"
                        :href="uploadImageUrl"
                        class="kgr-button kgr-button--accent"
                        style="margin-right: 10px"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        {{ $t('Neues Foto hinzufügen') }}
                    </a>
                    <a
                        v-if="album.hasUploadAccess"
                        :href="addVideoUrl"
                        class="kgr-button kgr-button--accent"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        {{ $t('Neues Video hinzufügen') }}
                    </a>
                </div>
            </div>

            <div
                v-if="album.hasWriteAccess || (translateTo && !loading)"
                class="app-contentHeader kgr-hidden-print"
                style="justify-content: flex-end"
            >
                <div class="kgr-postfix">
                    <div
                        v-if="album.hasWriteAccess && album.mediaCount > 0"
                        class="applet"
                        style="margin-right: 20px"
                    >
                        <kgr-batch-delete-buttons
                            selector-class="image-select"
                            delete-path="/kindergartens/gallery-images"
                        />
                    </div>
                    <a
                        v-if="album.hasWriteAccess"
                        :href="editUrl"
                        class="kgr-button kgr-button--accent"
                    >
                        <kgr-icon
                            icon-class="edit"
                            class="kgr-button__icon kgr-button__icon--left"
                            small
                        />
                        {{ $t('edit') }}
                    </a>
                </div>
            </div>

            <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ !showOriginal && translateTo && album.translations?.title ? album.translations.title : album.title }}
                <kgr-publication-icon
                    v-if="!hasRoleParent"
                    :clearing="album.clearing"
                />
            </h3>
            <div class="app-meta kgr-row">
                {{ getParsedDate(album.createdAt) }},
                {{ album.groups.length > 0 ? album.groups.map(g => g.name).join(', ') : $t('Groups.all') }}
                <template v-if="album.creator">
                    , {{ album.creator }}
                </template>
            </div>
            <p class="kgr-paragraph">
                {{ !showOriginal && translateTo && album.translations?.description ? album.translations.description : album.description }}
            </p>

            <div
                v-if="album.mediaItems.length > 0"
                class="kgr-grid kgr-grid--inline kgr-grid--gutters thumb-galerie"
            >
                <article
                    v-for="mediaItem in album.mediaItems"
                    :key="mediaItem.identity"
                    class="app-gridCard kgr-grid__cell"
                >
                    <div class="kgr-card kgr-backgroundColor kgr-backgroundColor--white">
                        <a
                            v-if="mediaItem.type === 'video'"
                            class="kgr-card__image kgr-card__image--full lightbox video"
                            :title="mediaItem.title"
                            :rel="`fancybox_${album.id}`"
                            :href="`https://player.vimeo.com/video/${mediaItem.externalId}?badge=0&autoplay=1&autopause=0&player_id=0`"
                        >
                            <img :src="getVideoThumbnailUrl(mediaItem)" />
                        </a>

                        <a
                            v-else-if="mediaItem.type === 'image'"
                            class="kgr-card__image kgr-card__image--full lightbox"
                            :title="mediaItem.title"
                            :rel="`fancybox_${album.id}`"
                            :href="getImageLightboxUrl(mediaItem)"
                        >
                            <img :src="getImageThumbnailUrl(mediaItem)" />
                        </a>

                        <h3 class="kgr-card__name kgr-card__name--noSeparator kgr-card__name--small">
                            {{ mediaItem.title }}
                        </h3>

                        <div class="kgr-card__contents"></div>

                        <div
                            v-if="album.hasWriteAccess && !hasRoleParent"
                            class="kgr-card__footer"
                        >
                            <div class="kgr-card__footerContents kgr-postfix kgr-postfix--center kgr-postfix--smallGutters">
                                <div class="kgr-postfix__fluid">
                                    <label
                                        v-if="mediaItem.type === 'image'"
                                        class="kgr-checkbox--inline"
                                    >
                                        <input
                                            type="checkbox"
                                            style="display: none"
                                            class="kgr-checkbox__control image-select"
                                            :value="mediaItem.identity"
                                        />
                                        <span class="kgr-checkbox__label"></span>
                                    </label>
                                </div>
                                <div class="kgr-postfix__fixed">
                                    <kgr-icon
                                        :icon-class="mediaItem.type === 'video' ? 'video-gallery' : 'photo-gallery'"
                                        :title="$t(`Gallery.mediaItemType.${mediaItem.type}`)"
                                        small
                                        disabled
                                    />
                                </div>
                                <div class="kgr-postfix__fixed">
                                    <a
                                        :href="getEditUrl(mediaItem)"
                                        :title="$t('edit')"
                                    >
                                        <kgr-icon
                                            icon-class="edit"
                                            hover-accent
                                            small
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </article>
            </div>
            <div
                v-else
                class="kgr-emptyMessage"
            >
                {{ $t('Gallery.noEntriesExist') }}
            </div>
        </div>
    </div>
</template>

<script>
import { format, parseISO } from '@legacy/js/date/date-fns.js';
import BatchDeleteButtons from '../../../components/kindergarten/BatchDeleteButtons.vue';
import GalleryRepository from '../../../js/repository/GalleryRepository.js';
import Icon from '../../../components/common/icon/Icon.vue';
import Loading from '../../../components/common/Loading.vue';
import PublicationIcon from '../../../components/common/icon/PublicationIcon.vue';
import { getLanguageFromCookie } from '../../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-batch-delete-buttons': BatchDeleteButtons,
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-publication-icon': PublicationIcon,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
        albumId: {
            type: Number,
            required: true,
        },
        hasGeneralWriteAccess: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasRoleParent: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            loading: true,
            showOriginal: false,
            translateTo: null,
            album: {
                id: null,
                title: '',
                description: '',
                groups: [],
                creator: '',
                createdAt: '',
                clearing: null,
                hasWriteAccess: false,
                hasUploadAccess: false,
                mediaItems: [],
                mediaCount: 0,
            },
        };
    },
    computed: {
        backUrl() {
            return '/backend/gallery/albums';
        },
        newAlbumUrl() {
            return '/backend/gallery/albums/new';
        },
        editUrl() {
            return `/backend/gallery/album/${this.album.id}/edit`;
        },
        uploadImageUrl() {
            return `/backend/gallery/images/new/${this.album.id}`;
        },
        addVideoUrl() {
            return `/backend/gallery/videos/new/${this.album.id}`;
        },
        isMissingTranslation() {
            const hasTitle = this.album.title !== null && this.album.title.trim() !== '';
            const hasDescription = this.album.description !== null && this.album.description.trim() !== '';

            const missingTitle = hasTitle && !this.album.translations?.title;
            const missingDescription = hasDescription && !this.album.translations?.description;

            return missingTitle || missingDescription;
        },
    },
    created() {
        const savedLang = getLanguageFromCookie();
        if (savedLang && savedLang !== 'de_DE') {
            this.translateTo = savedLang;
            this.translate();
        } else {
            this.load();
        }
    },
    updated() {
        this.initFancybox();
    },
    methods: {
        async translate() {
            await this.load();
            if (!this.isMissingTranslation) {
                return;
            }
            this.loading = true;
            await GalleryRepository.createTranslations([this.albumId], this.translateTo);
            this.load();
        },
        async load() {
            this.loading = true;
            try {
                this.album = await GalleryRepository.find(this.kindergartenId, this.albumId, { lang: this.translateTo });
                await this.$nextTick();
                this.initFancybox();
            } catch {
                this.$notify.error('error.loadingData');
            } finally {
                this.loading = false;
            }
        },
        initFancybox() {
            // Use jQuery fancybox (already loaded via Twig)
            this.$nextTick(() => {
                if (window.$ && window.$.fancybox) {
                    $('.thumb-galerie a.lightbox:not(.video)').fancybox({
                        titlePosition: 'inside',
                        type: 'image',
                    });
                    $('.thumb-galerie a.lightbox.video').fancybox({
                        titlePosition: 'inside',
                        type: 'iframe',
                    });
                }
            });
        },
        getImageThumbnailUrl(mediaItem) {
            return `/backend/thumbnail/image/${mediaItem.id}/card_full/${mediaItem.hash}`;
        },
        getImageLightboxUrl(mediaItem) {
            return `/backend/thumbnail/image/${mediaItem.id}/big/${mediaItem.hash}`;
        },
        getVideoThumbnailUrl(mediaItem) {
            if (mediaItem.fileName) {
                return `/backend/thumbnail/video/${mediaItem.id}/card_full/${mediaItem.fileName}`;
            }
            return '/img/video-placeholder.png';
        },
        getEditUrl(mediaItem) {
            if (mediaItem.type === 'video') {
                return `/backend/gallery/video/${mediaItem.identity}/edit`;
            }
            return `/backend/gallery/image/${mediaItem.identity}/edit`;
        },
        getParsedDate(date) {
            if (!date) {
                return '';
            }
            const parsedDate = parseISO(date);
            return format(parsedDate, 'dd.MM.yyyy');
        },
    },
};
</script>

<style scoped>
.app-gridCard {
    width: 350px;
}

.kgr-card__image img {
    width: 100%;
    height: auto;
    object-fit: cover;
}
</style>
